/**
 * Theme accessibility enhancements (DSRS UAT-ready items).
 */
(function () {
  function initSkipToContent() {
    const skipLink = document.querySelector('.skip-to-content-link');
    const main = document.getElementById('MainContent');
    if (!skipLink || !main) return;

    if (!main.hasAttribute('tabindex')) {
      main.setAttribute('tabindex', '-1');
    }

    skipLink.addEventListener('click', (event) => {
      event.preventDefault();
      main.focus({ preventScroll: true });
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      main.scrollIntoView({ behavior, block: 'start' });
    });
  }

  function fixNewWindowLinks() {
    document.querySelectorAll('a[target="_blank"]').forEach((link) => {
      const rel = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
      rel.add('noopener');
      rel.add('noreferrer');
      link.setAttribute('rel', Array.from(rel).join(' '));

      const accessibleName = `${link.textContent || ''} ${link.getAttribute('aria-label') || ''}`.toLowerCase();
      if (!accessibleName.includes('new window')) {
        const describedBy = new Set((link.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
        describedBy.add('a11y-new-window-message');
        link.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
      }
    });
  }

  function fixThirdPartyHeadings() {
    document.querySelectorAll('.klaviyo-form h1').forEach((heading) => {
      const replacement = document.createElement('h2');
      Array.from(heading.attributes).forEach((attribute) => {
        replacement.setAttribute(attribute.name, attribute.value);
      });
      while (heading.firstChild) replacement.appendChild(heading.firstChild);
      heading.replaceWith(replacement);
    });
  }

  function fixShopifyForms() {
    document.querySelectorAll('shopify-forms-embed').forEach((embed) => {
      const root = embed.shadowRoot;
      if (!root) return;

      root.querySelectorAll('[role="dialog"][aria-modal="true"]').forEach((container) => {
        container.setAttribute('role', 'region');
        container.removeAttribute('aria-modal');
        if (!container.getAttribute('aria-label')) {
          container.setAttribute('aria-label', 'Request a quote form');
        }
        if (container.getAttribute('tabindex') === '-1') container.removeAttribute('tabindex');
      });

      root.querySelectorAll('textarea[name="custom#additional_project_details"]').forEach((textarea) => {
        const labels = Array.from(root.querySelectorAll('label'));
        const existingLabel = labels.find(
          (label) => label.htmlFor === textarea.name || (textarea.id && label.htmlFor === textarea.id)
        );
        if (!textarea.id) textarea.id = textarea.name;
        if (existingLabel) {
          existingLabel.htmlFor = textarea.id;
        } else if (textarea.parentElement) {
          const label = document.createElement('label');
          label.htmlFor = textarea.id;
          label.textContent = 'Additional project details';
          label.style.display = 'block';
          label.style.marginBottom = '0.5rem';
          textarea.parentElement.insertBefore(label, textarea);
        }
      });
    });
  }

  function removeEmptyHeadings() {
    document.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
      if (!heading.textContent.trim() && !heading.querySelector('img[alt]:not([alt=""])')) {
        heading.remove();
      }
    });
  }

  function cleanupProductDescriptionTables() {
    document
      .querySelectorAll('.product-tabs__full-description table, .product-tabs__panel-content table, .rte table')
      .forEach((table) => {
        table.querySelectorAll('tr').forEach((row) => {
          // Image-only documentation links, media, and form controls are content
          // even when textContent is blank. Preserve rows participating in a
          // rowspan so removing a spacer cannot change the table's cell layout.
          const hasNonTextContent = row.querySelector(
            'img, svg, video, audio, iframe, object, embed, canvas, input, select, textarea, button, a[href], [aria-label], [aria-labelledby]'
          );
          const hasRowSpan = Array.from(table.querySelectorAll('[rowspan]')).some(
            (cell) => cell.getAttribute('rowspan') !== '1'
          );
          if (!row.textContent.trim() && !hasNonTextContent && !hasRowSpan) {
            row.remove();
          }
        });

        table.querySelectorAll('a').forEach((link) => {
          const text = (link.textContent || '').trim();
          const hasImageOnly = link.querySelector('img') && !text;
          if (!hasImageOnly) return;

          const rowLabel = link.closest('tr')?.querySelector('th, td')?.textContent?.trim();
          const href = link.getAttribute('href') || '';
          const fileName = href.split('/').pop()?.split('?')[0] || 'documentation';
          link.setAttribute('aria-label', rowLabel ? `${rowLabel} documentation` : fileName);
        });
      });
  }

  function fixMediaAltFallbacks() {
    document.querySelectorAll('.product__media img').forEach((img) => {
      const slide = img.closest('[data-shopify-media-id]');
      const mapElement = slide?.closest('media-gallery')?.querySelector('[id^="ProductMediaAltMap-"]');
      if (mapElement && slide) {
        try {
          const altMap = JSON.parse(mapElement.textContent);
          const mappedAlt = altMap[slide.getAttribute('data-shopify-media-id')];
          if (mappedAlt) {
            img.alt = mappedAlt;
            return;
          }
        } catch (error) {
          /* ignore invalid JSON */
        }
      }

      if (img.alt && img.alt.trim()) return;

      const productTitle = document.querySelector('.product__title, h1.product__title')?.textContent?.trim();
      if (!productTitle) return;

      const index = slide?.id?.match(/(\d+)$/)?.[1];
      img.alt = index ? `${productTitle} - image ${index}` : productTitle;
    });
  }

  function fixFlickityButtons() {
    document.querySelectorAll('.flickity-button.previous').forEach((button) => {
      button.setAttribute('aria-label', 'Previous');
    });
    document.querySelectorAll('.flickity-button.next').forEach((button) => {
      button.setAttribute('aria-label', 'Next');
    });
  }

  const observedChatRoots = new WeakSet();

  function fixChatWidget() {
    // BestChat renders in an open shadow root; document-level selectors cannot reach it.
    const root = document.getElementById('smart-bot-container')?.shadowRoot;
    if (!root) return;

    const trigger = root.querySelector('[class*="_chat-icon-container_"][role="button"]');
    if (trigger) {
      const visibleText = trigger.querySelector('[class*="_text_"]')?.textContent?.trim();
      const label = visibleText ? `${visibleText} - Open chat window` : 'Open chat window';
      if (trigger.getAttribute('aria-label') !== label) trigger.setAttribute('aria-label', label);
    }

    if (observedChatRoots.has(root)) return;
    observedChatRoots.add(root);
    const style = document.createElement('style');
    style.textContent = `
      [class*="_notification-card_"]:focus-within [class*="_close-wrap_"] {
        display: block !important;
        opacity: 1 !important;
        visibility: visible !important;
      }
      [aria-label="Close notification"]:focus-visible {
        outline: 2px solid #0a2239;
        outline-offset: 2px;
      }
      @media (max-height: 480px) {
        [class*="_notification-card_"] {
          max-height: calc(100vh - 130px) !important;
          box-sizing: border-box !important;
          overflow-y: auto !important;
          padding-top: 40px !important;
        }
        [class*="_notification-card_"] [class*="_close-wrap_"] {
          top: 8px !important;
          right: 8px !important;
          transform: none !important;
        }
      }
    `;
    root.appendChild(style);
    // Preserve the vendor's existing close/activation handlers and watch async renders.
    new MutationObserver(fixChatWidget).observe(root, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['aria-label'],
    });
  }

  function fixInstafeedFocus() {
    document
      .querySelectorAll('#instafeed-app [role="button"], .instafeed-lightbox, [class*="instafeed"] [role="button"]')
      .forEach((node) => {
        if (node.tagName.toLowerCase() === 'div') {
          if (!node.hasAttribute('tabindex')) {
            node.setAttribute('tabindex', '0');
          }
        }

        // Instafeed already handles keyboard activation. Adding click synthesis here
        // duplicated that behavior on every observed DOM mutation (DSRS-274).
      });
  }

  function runDomFixes() {
    removeEmptyHeadings();
    cleanupProductDescriptionTables();
    fixMediaAltFallbacks();
    fixFlickityButtons();
    fixNewWindowLinks();
    fixThirdPartyHeadings();
    fixShopifyForms();
    fixChatWidget();
    fixInstafeedFocus();
  }

  function observeThirdPartyWidgets() {
    const observer = new MutationObserver(() => {
      fixChatWidget();
      fixInstafeedFocus();
      fixFlickityButtons();
      fixNewWindowLinks();
      fixThirdPartyHeadings();
      fixShopifyForms();
      fixMediaAltFallbacks();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    initSkipToContent();
    runDomFixes();
    observeThirdPartyWidgets();

    let formAttempts = 0;
    const formTimer = window.setInterval(() => {
      fixShopifyForms();
      formAttempts += 1;
      if (formAttempts >= 20) window.clearInterval(formTimer);
    }, 500);
  });
})();
